import { useEffect, useState } from 'react'
import { useWindowWidth } from '../../../lib/useWindowWidth'
import { useNavigate } from 'react-router-dom'
import {
  reportingService,
  type DashboardSummaryData,
  type TelecallerPerformanceData,
  type ConversionFunnelData,
  type MyPerformanceData,
} from '../services/reports'
import { adminService, type Tenant, type Wallet as WalletType, type PlatformAuditEntry } from '../../identity/services/admin'
import { billingService } from '../../billing/services/billing'
import { usePendingDiscounts, APPROVER_ROLES } from '../../../contexts/PendingDiscountsContext'
import { automationService, type AutomationStats } from '../../automation/services/automation'
import type { Subscription } from '../../../types'
import { authService } from '../../identity/services/auth'
import { UserRole } from '../../../types'
import { LOW_WALLET_THRESHOLD } from '../../../lib/constants'
import {
  Users, PhoneCall, FileCheck, IndianRupee, Activity, TrendingUp,
  RefreshCw, Shield, Percent, ChevronRight, MessageSquare,
  Target, Clock, CheckCircle2, XCircle, BarChart2, AlertTriangle,
} from 'lucide-react'
import { fraudService, isNegConfTerminal, type NegativeConfirmation } from '../../fraud/services/fraud'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts'

// ── Shared helpers ────────────────────────────────────────────────────────────

const fmt = (n: number) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n)

const fmtDateTime = (s: string) =>
  new Date(s).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })

const ACTION_LABEL: Record<string, string> = {
  TENANT_CREATED:           'Tenant onboarded',
  WALLET_CREDITED:          'Wallet credited',
  SUBSCRIPTION_CREATED:     'Subscription created',
  TIER_ASSIGNED:            'Plan tier assigned',
  SUBSCRIPTION_SUSPENDED:   'Subscription suspended',
  SUBSCRIPTION_REACTIVATED: 'Subscription reactivated',
  SUBSCRIPTION_CANCELLED:   'Subscription cancelled',
}

const ACTION_COLOR: Record<string, string> = {
  TENANT_CREATED:           'var(--success-text)',
  WALLET_CREDITED:          'var(--info-text)',
  SUBSCRIPTION_CREATED:     'var(--info-text)',
  TIER_ASSIGNED:            'var(--brand-primary)',
  SUBSCRIPTION_SUSPENDED:   'var(--warning-text)',
  SUBSCRIPTION_REACTIVATED: 'var(--success-text)',
  SUBSCRIPTION_CANCELLED:   'var(--danger-text)',
}

// ── Compact metrics bar ───────────────────────────────────────────────────────

interface MetricItem {
  label: string
  value: string | number
  color?: string
  warning?: boolean
}

function MetricsBar({ items, loading }: { items: MetricItem[]; loading: boolean }) {
  return (
    <div className="card" style={{ padding: '12px 20px' }}>
      <div className="flex flex-wrap items-center gap-0 divide-x" style={{ '--tw-divide-opacity': 1 } as React.CSSProperties}>
        {items.map((item, i) => (
          <div key={i} className="flex flex-col items-start px-5 first:pl-0 last:pr-0" style={{ minWidth: 100 }}>
            {loading
              ? <div className="skeleton h-5 w-14 rounded mb-1" />
              : <span className="text-lg font-semibold tabular-nums leading-tight"
                  style={{ color: item.color ?? (item.warning ? 'var(--warning-text)' : 'var(--text-primary)') }}>
                  {item.value}
                </span>
            }
            <span className="text-xs mt-0.5" style={{ color: 'var(--text-tertiary)' }}>{item.label}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

// ── BACKEND_ADMIN dashboard ───────────────────────────────────────────────────

function AdminDashboard() {
  const windowWidth = useWindowWidth()
  const isMobile = windowWidth < 640
  const chartBarSize = isMobile ? 20 : 40
  const chartTickSize = isMobile ? 10 : 12

  const [tenants, setTenants]           = useState<Tenant[]>([])
  const [wallets, setWallets]           = useState<WalletType[]>([])
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([])
  const [recentActivity, setRecentActivity] = useState<PlatformAuditEntry[]>([])
  const [loading, setLoading]           = useState(true)

  const load = async () => {
    setLoading(true)
    try {
      const [t, w, s, a] = await Promise.all([
        adminService.listTenants(),
        adminService.listWallets(),
        billingService.listSubscriptions(),
        adminService.getPlatformAuditLogs({ page: 1 }),
      ])
      setTenants(t)
      setWallets(w)
      setSubscriptions(s)
      setRecentActivity(a.items.slice(0, 6))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [])

  // Derived stats
  const activeTenants   = tenants.filter(t => t.is_active).length
  const totalBalance    = wallets.reduce((sum, w) => sum + Number(w.balance), 0)
  const lowBalance      = wallets.filter(w => Number(w.balance) < LOW_WALLET_THRESHOLD).length
  const coreSubs        = subscriptions.filter(s => s.service_type === 'CORE')
  const activePlans     = coreSubs.filter(s => s.status?.toLowerCase() === 'active').length
  const suspendedPlans  = coreSubs.filter(s => s.status?.toLowerCase() === 'suspended').length
  const unassignedCount = tenants.filter(t => !coreSubs.find(s => s.tenant_id === t.id)).length

  // Tier breakdown for bar chart
  const tierCounts: Record<string, number> = {}
  coreSubs.filter(s => s.pricing_tier).forEach(s => {
    const t = s.pricing_tier!
    tierCounts[t] = (tierCounts[t] ?? 0) + 1
  })
  const tierChartData = Object.entries(tierCounts).map(([name, count]) => ({
    name: name.charAt(0).toUpperCase() + name.slice(1), count,
  }))
  if (unassignedCount > 0) tierChartData.push({ name: 'No plan', count: unassignedCount })

  // Wallet health — sorted by balance ascending (most at-risk first)
  const tenantById = Object.fromEntries(tenants.map(t => [t.id, t]))
  const walletHealth = [...wallets]
    .sort((a, b) => Number(a.balance) - Number(b.balance))
    .slice(0, 6)

  return (
    <div className="space-y-6">

      {/* Header */}
      <div className="section-header">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <Shield className="h-6 w-6" style={{ color: 'var(--brand-primary)' }} />
            Platform Overview
            <button onClick={load} disabled={loading} className="btn-icon" title="Refresh" aria-label="Refresh" style={{ marginLeft: 'auto' }}>
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </h1>
          <p className="page-subtitle">Health and status of all tenants on the platform</p>
        </div>
      </div>

      {/* Compact metrics strip */}
      <MetricsBar loading={loading} items={[
        { label: 'Active tenants',    value: `${activeTenants} / ${tenants.length}`,
          color: 'var(--brand-primary)' },
        { label: 'Active plans',      value: activePlans,
          color: 'var(--success-text)' },
        { label: 'Platform balance',  value: fmt(totalBalance),
          color: 'var(--info-text)' },
        { label: 'Low balance',       value: lowBalance,
          color: lowBalance > 0 ? 'var(--warning-text)' : 'var(--success-text)' },
        { label: 'Without plan',      value: unassignedCount,
          color: unassignedCount > 0 ? 'var(--danger-text)' : 'var(--text-secondary)' },
        { label: 'Suspended',         value: suspendedPlans,
          color: suspendedPlans > 0 ? 'var(--danger-text)' : 'var(--text-secondary)' },
      ]} />

      {/* Middle row: tier breakdown + recent activity */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">

        {/* Plan tier breakdown */}
        <div className="lg:col-span-2 card">
          <h2 className="text-sm font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
            Plan Distribution
          </h2>
          {loading ? (
            <div className="h-52 flex items-center justify-center">
              <div className="spinner" />
            </div>
          ) : tierChartData.length === 0 ? (
            <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>No subscriptions yet</p>
          ) : (
            <div className="h-52">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={tierChartData} barSize={chartBarSize}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border-default)" vertical={false} />
                  <XAxis dataKey="name" stroke="var(--text-tertiary)"
                    tick={{ fontSize: chartTickSize, fill: 'var(--text-tertiary)' }} axisLine={false} tickLine={false} />
                  <YAxis stroke="var(--text-tertiary)"
                    tick={{ fontSize: chartTickSize, fill: 'var(--text-tertiary)' }} axisLine={false} tickLine={false} width={24} allowDecimals={false} />
                  <Tooltip
                    cursor={{ fill: 'var(--surface-hover)' }}
                    contentStyle={{ background: 'var(--surface-card)', border: '1px solid var(--border-default)', borderRadius: 6, fontSize: 12 }}
                  />
                  <Bar dataKey="count" name="Tenants" fill="var(--brand-primary)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* Recent platform activity */}
        <div className="lg:col-span-3 card">
          <h2 className="text-sm font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
            Recent Activity
          </h2>
          {loading ? (
            <div className="space-y-3">
              {[1,2,3].map(i => <div key={i} className="skeleton h-10 rounded" />)}
            </div>
          ) : recentActivity.length === 0 ? (
            <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>No activity yet</p>
          ) : (
            <div className="space-y-1">
              {recentActivity.map(entry => (
                <div key={entry.id} className="flex items-start gap-3 py-2"
                  style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                  <div className="w-1.5 h-1.5 rounded-full mt-1.5 shrink-0"
                    style={{ background: ACTION_COLOR[entry.action] ?? 'var(--text-tertiary)' }} />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm" style={{ color: 'var(--text-primary)' }}>
                      {ACTION_LABEL[entry.action] ?? entry.action}
                      {entry.tenant_name && (
                        <span className="font-medium"> · {entry.tenant_name}</span>
                      )}
                    </p>
                    <p className="text-xs mt-0.5" style={{ color: 'var(--text-tertiary)' }}>
                      {entry.actor} · {entry.timestamp ? fmtDateTime(entry.timestamp) : '—'}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Bottom: wallet health table */}
      <div className="table-card">
        <div className="table-card-header">
          <h2 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
            Wallet Health
          </h2>
          <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
            Sorted by balance — lowest first
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th>Tenant</th>
                <th>Balance</th>
                <th>Plan</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={4} className="py-8 text-center">
                    <div className="spinner mx-auto" />
                  </td>
                </tr>
              ) : walletHealth.length === 0 ? (
                <tr>
                  <td colSpan={4} className="py-8 text-center" style={{ color: 'var(--text-tertiary)' }}>
                    No wallets yet
                  </td>
                </tr>
              ) : (
                walletHealth.map(w => {
                  const tenant   = tenantById[w.tenant_id]
                  const balance  = Number(w.balance)
                  const isLow    = balance < 3000
                  const coreSub  = coreSubs.find(s => s.tenant_id === w.tenant_id)
                  const subStatus = coreSub?.status?.toLowerCase()
                  return (
                    <tr key={w.id}>
                      <td>
                        <p className="text-primary-data">{tenant?.name ?? `Tenant #${w.tenant_id}`}</p>
                        <p className="font-mono text-xs mt-0.5" style={{ color: 'var(--text-tertiary)' }}>
                          {tenant?.domain ?? '—'}
                        </p>
                      </td>
                      <td>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold tabular-nums"
                            style={{ color: isLow ? 'var(--danger-text)' : 'var(--success-text)' }}>
                            {fmt(balance)}
                          </span>
                          {isLow && (
                            <span className="badge-danger" style={{ fontSize: 10, padding: '1px 5px' }}>
                              Low
                            </span>
                          )}
                        </div>
                      </td>
                      <td>
                        {coreSub?.pricing_tier
                          ? <span className="badge-info capitalize">{coreSub.pricing_tier}</span>
                          : <span style={{ color: 'var(--text-tertiary)', fontSize: 13 }}>No plan</span>
                        }
                      </td>
                      <td>
                        {subStatus
                          ? <span className={
                              subStatus === 'active'    ? 'badge-success' :
                              subStatus === 'suspended' ? 'badge-warning' : 'badge-neutral'
                            } style={{ textTransform: 'capitalize' }}>{subStatus}</span>
                          : <span style={{ color: 'var(--text-tertiary)', fontSize: 13 }}>—</span>
                        }
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

// ── Operational dashboard (IH / TL / TC) ─────────────────────────────────────

const STAT_CARDS = [
  { key: 'total_leads',          label: 'Total Leads',     icon: Users,        accent: 'var(--brand-600)',    accentBg: 'var(--brand-primary-subtle)', currency: false },
  { key: 'new_leads_today',      label: 'New Today',       icon: Activity,     accent: 'var(--success-text)', accentBg: 'var(--success-bg)',           currency: false },
  { key: 'calls_made_today',     label: 'Calls Today',     icon: PhoneCall,    accent: 'var(--info-text)',    accentBg: 'var(--info-bg)',               currency: false },
  { key: 'policies_issued_today',label: 'Policies Issued', icon: FileCheck,    accent: 'var(--success-text)', accentBg: 'var(--success-bg)',           currency: false },
  { key: 'total_revenue_today',  label: 'Revenue Today',   icon: IndianRupee,  accent: 'var(--brand-600)',    accentBg: 'var(--brand-primary-subtle)', currency: true  },
  { key: 'active_telecallers',   label: 'Active Agents',   icon: TrendingUp,   accent: 'var(--info-text)',    accentBg: 'var(--info-bg)',               currency: false },
] as const

// ── Pending discounts CTA ─────────────────────────────────────────────────────

function timeAgoMs(isoString: string): number {
  return Date.now() - new Date(isoString).getTime()
}

function PendingDiscountsCTA() {
  const { pending } = usePendingDiscounts()
  const navigate = useNavigate()
  const currentUser = authService.getCurrentUser()

  if (!currentUser || !APPROVER_ROLES.includes(currentUser.role as UserRole)) return null
  if (pending.length === 0) return null

  const oldestMs = Math.max(...pending.map(p => timeAgoMs(p.created_at ?? new Date().toISOString())))
  const oldestHours = oldestMs / 3_600_000
  const oldestLabel = oldestHours >= 1
    ? `${Math.floor(oldestHours)}h ago`
    : `${Math.floor(oldestMs / 60_000)}m ago`

  return (
    <div
      className="card flex items-center justify-between gap-4"
      style={{ borderLeft: '4px solid var(--brand-primary)', background: 'var(--surface-card)' }}
    >
      <div className="flex items-center gap-3">
        <Percent className="h-4 w-4 shrink-0" style={{ color: 'var(--brand-primary)' }} />
        <div>
          <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
            {pending.length} OD Discount Approval{pending.length !== 1 ? 's' : ''} Pending
          </p>
          <p className="text-xs mt-0.5" style={{ color: 'var(--text-secondary)' }}>
            Oldest request: {oldestLabel}
          </p>
        </div>
      </div>
      <button
        onClick={() => navigate('/discounts')}
        className="btn-secondary shrink-0 flex items-center gap-1"
        style={{ fontSize: 12, height: 30 }}
      >
        Review <ChevronRight className="h-3.5 w-3.5" />
      </button>
    </div>
  )
}

// ── Negative confirmations CTA ────────────────────────────────────────────────

function NegativeConfirmationsCTA() {
  const [active, setActive] = useState<NegativeConfirmation[]>([])
  const navigate = useNavigate()
  const currentUser = authService.getCurrentUser()

  const canSee = [UserRole.DEALER_PRINCIPAL, UserRole.INSURANCE_HEAD].includes(currentUser?.role as UserRole)

  useEffect(() => {
    if (!canSee) return
    fraudService.listNegativeConfirmations()
      .then(items => setActive(items.filter(n =>
        n.customer_response === 'DISPUTED' && !isNegConfTerminal(n.investigation_status)
      )))
      .catch(() => {})
  }, [canSee])

  if (!canSee || active.length === 0) return null

  // Stays red while any dispute hasn't been picked up yet; turns yellow only
  // once every remaining active dispute is under investigation.
  const allInvestigating = active.every(n => n.investigation_status === 'INVESTIGATING')
  const colorVar = allInvestigating ? 'var(--warning-text)' : 'var(--danger-text)'
  const bgVar    = allInvestigating ? 'var(--warning-bg)'   : 'var(--danger-bg)'

  return (
    <div
      className="card flex items-center justify-between gap-4"
      style={{ borderLeft: `4px solid ${colorVar}`, background: bgVar }}
    >
      <div className="flex items-center gap-3">
        <AlertTriangle className="h-4 w-4 shrink-0" style={{ color: colorVar }} />
        <div>
          <p className="text-sm font-semibold" style={{ color: colorVar }}>
            {active.length} Customer{active.length !== 1 ? 's' : ''} Disputed a Policy Decline
          </p>
          <p className="text-xs mt-0.5" style={{ color: colorVar, opacity: 0.8 }}>
            {allInvestigating
              ? 'Under investigation'
              : 'Customer claims payment was made — immediate review required'}
          </p>
        </div>
      </div>
      <button
        onClick={() => navigate('/negative-confirmations')}
        className="btn-secondary shrink-0 flex items-center gap-1"
        style={{ fontSize: 12, height: 30 }}
      >
        Review <ChevronRight className="h-3.5 w-3.5" />
      </button>
    </div>
  )
}

// ── Automation stats CTA ──────────────────────────────────────────────────────

function AutomationStatsCTA() {
  const [stats, setStats] = useState<AutomationStats | null>(null)
  const navigate = useNavigate()
  const currentUser = authService.getCurrentUser()

  const canSee = [UserRole.DEALER_PRINCIPAL, UserRole.INSURANCE_HEAD].includes(currentUser?.role as UserRole)

  useEffect(() => {
    if (!canSee) return
    automationService.getStats().then(setStats).catch(() => {})
  }, [canSee])

  if (!stats || stats.active_rules === 0) return null

  return (
    <div
      className="card flex items-center justify-between gap-4"
      style={{
        borderLeft: `4px solid ${stats.today_failed > 0 ? 'var(--danger-text)' : 'var(--success-text)'}`,
        background: stats.today_failed > 0 ? 'var(--danger-bg)' : 'var(--surface-card)',
      }}
    >
      <div className="flex items-center gap-3">
        <MessageSquare className="h-4 w-4 shrink-0"
          style={{ color: stats.today_failed > 0 ? 'var(--danger-text)' : 'var(--success-text)' }} />
        <div>
          <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
            WhatsApp Automation · {stats.active_rules} active rule{stats.active_rules !== 1 ? 's' : ''}
          </p>
          <p className="text-xs mt-0.5" style={{ color: 'var(--text-secondary)' }}>
            Today: {stats.today_sent} sent
            {stats.today_failed > 0 && (
              <span style={{ color: 'var(--danger-text)' }}> · {stats.today_failed} failed</span>
            )}
            {stats.today_skipped > 0 && ` · ${stats.today_skipped} skipped`}
          </p>
        </div>
      </div>
      <button
        onClick={() => navigate('/settings/whatsapp?tab=logs')}
        className="btn-secondary shrink-0 flex items-center gap-1"
        style={{ fontSize: 12, height: 30 }}
      >
        View Logs <ChevronRight className="h-3.5 w-3.5" />
      </button>
    </div>
  )
}

// ── Dealer Principal dashboard ────────────────────────────────────────────────
// Revenue-first, business-owner view. Same data source, different emphasis.

const DP_STAT_CARDS = [
  { key: 'total_revenue_today',  label: 'Revenue Today',   icon: IndianRupee,  accent: 'var(--brand-600)',    accentBg: 'var(--brand-primary-subtle)', currency: true  },
  { key: 'policies_issued_today',label: 'Policies Today',  icon: FileCheck,    accent: 'var(--success-text)', accentBg: 'var(--success-bg)',           currency: false },
  { key: 'total_leads',          label: 'Total Leads',     icon: Users,        accent: 'var(--info-text)',    accentBg: 'var(--info-bg)',               currency: false },
  { key: 'new_leads_today',      label: 'New Today',       icon: Activity,     accent: 'var(--success-text)', accentBg: 'var(--success-bg)',           currency: false },
  { key: 'calls_made_today',     label: 'Calls Made',      icon: PhoneCall,    accent: 'var(--info-text)',    accentBg: 'var(--info-bg)',               currency: false },
  { key: 'active_telecallers',   label: 'Active Agents',   icon: TrendingUp,   accent: 'var(--brand-600)',    accentBg: 'var(--brand-primary-subtle)', currency: false },
] as const

function DPDashboard() {
  const navigate = useNavigate()
  const windowWidth = useWindowWidth()
  const isMobile = windowWidth < 640
  const chartBarSize = isMobile ? 16 : 32
  const chartTickSize = isMobile ? 10 : 11

  const [summary, setSummary]         = useState<DashboardSummaryData | null>(null)
  const [performance, setPerformance] = useState<TelecallerPerformanceData[]>([])
  const [funnel, setFunnel]           = useState<ConversionFunnelData[]>([])

  useEffect(() => {
    Promise.all([
      reportingService.getDashboardSummary(),
      reportingService.getTelecallerPerformance(),
      reportingService.getConversionFunnel(),
    ]).then(([s, p, f]) => {
      setSummary(s)
      setPerformance(Array.isArray(p) ? p : [])
      setFunnel(Array.isArray(f) ? f : [])
    }).catch(() => {
      // Individual failures: fetch each independently so partial data still shows
      reportingService.getDashboardSummary().then(setSummary).catch(() => {})
      reportingService.getTelecallerPerformance().then(p => setPerformance(Array.isArray(p) ? p : [])).catch(() => {})
      reportingService.getConversionFunnel().then(f => setFunnel(Array.isArray(f) ? f : [])).catch(() => {})
    })
  }, [])

  const getValue = (card: typeof DP_STAT_CARDS[number]) => {
    if (!summary) return '—'
    const raw = summary[card.key as keyof DashboardSummaryData]
    if (raw == null) return '—'
    const num = Number(raw)
    if (isNaN(num)) return '—'
    if (card.currency) return `₹${num.toLocaleString('en-IN')}`
    return num.toLocaleString('en-IN')
  }

  return (
    <div className="space-y-6">
      <div className="section-header">
        <div>
          <h1 className="page-title">Business Overview</h1>
          <p className="page-subtitle">Revenue, conversion, and team performance for today</p>
        </div>
        <button
          className="btn-secondary flex items-center gap-1.5"
          onClick={() => navigate('/leads')}
        >
          <Users className="h-3.5 w-3.5" />
          Active Leads
        </button>
      </div>

      <PendingDiscountsCTA />
      <NegativeConfirmationsCTA />
      <AutomationStatsCTA />

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
        {DP_STAT_CARDS.map(card => {
          const Icon = card.icon
          return (
            <div key={card.key} className="card" style={{ borderTop: `3px solid ${card.accent}` }}>
              <div className="w-8 h-8 rounded-md flex items-center justify-center mb-3" style={{ background: card.accentBg }}>
                <Icon className="h-4 w-4" style={{ color: card.accent }} />
              </div>
              <p className="stat-card-value">
                {summary ? getValue(card) : <span className="skeleton h-7 w-16 inline-block" />}
              </p>
              <p className="stat-card-label mt-1">{card.label}</p>
            </div>
          )
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 card">
          <h2 className="text-md font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>Conversion Funnel</h2>
          <div className="h-60">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={funnel} barSize={chartBarSize}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-default)" vertical={false} />
                <XAxis dataKey="stage" stroke="var(--text-tertiary)" tick={{ fontSize: chartTickSize, fill: 'var(--text-tertiary)' }} axisLine={false} tickLine={false} />
                <YAxis stroke="var(--text-tertiary)" tick={{ fontSize: chartTickSize, fill: 'var(--text-tertiary)' }} axisLine={false} tickLine={false} width={32} />
                <Tooltip cursor={{ fill: 'var(--surface-hover)' }}
                  contentStyle={{ background: 'var(--surface-card)', border: '1px solid var(--border-default)', borderRadius: 6, fontSize: 12, color: 'var(--text-primary)', boxShadow: 'var(--shadow-md)' }} />
                <Bar dataKey="count" fill="var(--brand-600)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="lg:col-span-2 card">
          <h2 className="text-md font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>Agent Performance</h2>
          <div className="space-y-2">
            {performance.length === 0 && (
              <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>No agent data yet</p>
            )}
            {performance.map(tc => (
              <div key={tc.user_id} className="flex items-center justify-between px-3 py-2.5 rounded-md" style={{ background: 'var(--surface-subtle)' }}>
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate" title={tc.name} style={{ color: 'var(--text-primary)' }}>{tc.name}</p>
                  <p className="text-xs mt-0.5" style={{ color: 'var(--text-tertiary)' }}>
                    {tc.calls_today} calls · {tc.total_calls} total
                  </p>
                </div>
                <div className="text-right shrink-0 ml-3">
                  <p className="text-sm font-semibold" style={{ color: 'var(--brand-600)' }}>{tc.conversion_rate}%</p>
                  <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                    {tc.policies_issued} issued
                    {tc.lost_leads > 0 && (
                      <span style={{ color: 'var(--danger-text)', marginLeft: 4 }}>
                        · {tc.lost_leads} lost
                      </span>
                    )}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

function OperationalDashboard() {
  const navigate = useNavigate()
  const windowWidth = useWindowWidth()
  const isMobile = windowWidth < 640
  const chartBarSize = isMobile ? 16 : 32
  const chartTickSize = isMobile ? 10 : 11

  const [summary, setSummary]         = useState<DashboardSummaryData | null>(null)
  const [performance, setPerformance] = useState<TelecallerPerformanceData[]>([])
  const [funnel, setFunnel]           = useState<ConversionFunnelData[]>([])

  useEffect(() => {
    Promise.all([
      reportingService.getDashboardSummary(),
      reportingService.getTelecallerPerformance(),
      reportingService.getConversionFunnel(),
    ]).then(([s, p, f]) => {
      setSummary(s)
      setPerformance(Array.isArray(p) ? p : [])
      setFunnel(Array.isArray(f) ? f : [])
    }).catch(() => {
      reportingService.getDashboardSummary().then(setSummary).catch(() => {})
      reportingService.getTelecallerPerformance().then(p => setPerformance(Array.isArray(p) ? p : [])).catch(() => {})
      reportingService.getConversionFunnel().then(f => setFunnel(Array.isArray(f) ? f : [])).catch(() => {})
    })
  }, [])

  const getValue = (card: typeof STAT_CARDS[number]) => {
    if (!summary) return '—'
    const raw = summary[card.key as keyof DashboardSummaryData]
    if (raw == null) return '—'
    const num = Number(raw)
    if (isNaN(num)) return '—'
    if (card.currency) return `₹${num.toLocaleString('en-IN')}`
    return num.toLocaleString('en-IN')
  }

  return (
    <div className="space-y-6">
      <div className="section-header">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="page-subtitle">Real-time overview of your insurance operations</p>
        </div>
        <button
          className="btn-secondary flex items-center gap-1.5"
          onClick={() => navigate('/leads')}
        >
          <Users className="h-3.5 w-3.5" />
          Active Leads
        </button>
      </div>

      <PendingDiscountsCTA />
      <NegativeConfirmationsCTA />
      <AutomationStatsCTA />

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
        {STAT_CARDS.map(card => {
          const Icon = card.icon
          return (
            <div key={card.key} className="card" style={{ borderTop: `3px solid ${card.accent}` }}>
              <div className="w-8 h-8 rounded-md flex items-center justify-center mb-3" style={{ background: card.accentBg }}>
                <Icon className="h-4 w-4" style={{ color: card.accent }} />
              </div>
              <p className="stat-card-value">
                {summary ? getValue(card) : <span className="skeleton h-7 w-16 inline-block" />}
              </p>
              <p className="stat-card-label mt-1">{card.label}</p>
            </div>
          )
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 card">
          <h2 className="text-md font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>Conversion Funnel</h2>
          <div className="h-60">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={funnel} barSize={chartBarSize}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-default)" vertical={false} />
                <XAxis dataKey="stage" stroke="var(--text-tertiary)" tick={{ fontSize: chartTickSize, fill: 'var(--text-tertiary)' }} axisLine={false} tickLine={false} />
                <YAxis stroke="var(--text-tertiary)" tick={{ fontSize: chartTickSize, fill: 'var(--text-tertiary)' }} axisLine={false} tickLine={false} width={32} />
                <Tooltip cursor={{ fill: 'var(--surface-hover)' }}
                  contentStyle={{ background: 'var(--surface-card)', border: '1px solid var(--border-default)', borderRadius: 6, fontSize: 12, color: 'var(--text-primary)', boxShadow: 'var(--shadow-md)' }} />
                <Bar dataKey="count" fill="var(--brand-600)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="lg:col-span-2 card">
          <h2 className="text-md font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>Agent Performance</h2>
          <div className="space-y-2">
            {performance.length === 0 && (
              <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>No agent data yet</p>
            )}
            {performance.map(tc => (
              <div key={tc.user_id} className="flex items-center justify-between px-3 py-2.5 rounded-md" style={{ background: 'var(--surface-subtle)' }}>
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate" title={tc.name} style={{ color: 'var(--text-primary)' }}>{tc.name}</p>
                  <p className="text-xs mt-0.5" style={{ color: 'var(--text-tertiary)' }}>
                    {tc.calls_today} calls today · {tc.total_calls} total
                  </p>
                </div>
                <div className="text-right shrink-0 ml-3">
                  <p className="text-sm font-semibold" style={{ color: 'var(--brand-600)' }}>{tc.conversion_rate}%</p>
                  <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                    {tc.policies_issued} issued
                    {tc.lost_leads > 0 && (
                      <span style={{ color: 'var(--danger-text)', marginLeft: 4 }}>
                        · {tc.lost_leads} lost
                      </span>
                    )}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Telecaller personal dashboard ────────────────────────────────────────────

const STATUS_LABEL: Record<string, string> = {
  NEW:                  'New',
  QUALIFIED:            'Qualified',
  QUOTATION_SENT:       'Quote Sent',
  CALLBACK_REQUESTED:   'Callback',
  WHATSAPP_QUALIFYING:  'WA Qualifying',
}

const STATUS_COLOR: Record<string, string> = {
  NEW:                  'var(--info-text)',
  QUALIFIED:            'var(--brand-600)',
  QUOTATION_SENT:       'var(--warning-text)',
  CALLBACK_REQUESTED:   'var(--brand-primary)',
  WHATSAPP_QUALIFYING:  'var(--text-tertiary)',
}

const DISPOSITION_LABEL: Record<string, string> = {
  CONNECTED:                  'Connected',
  NOT_CONNECTED:              'Not Connected',
  CALLBACK:                   'Callback',
  INTERESTED:                 'Interested',
  NOT_INTERESTED:             'Not Interested',
  POLICY_ISSUED:              'Policy Issued',
  CUSTOMER_BOOKED_ELSEWHERE:  'Booked Elsewhere',
  SOLD_VEHICLE:               'Sold Vehicle',
  DUE_DATE_LAPSED:            'Due Date Lapsed',
  WRONG_NUMBER:               'Wrong Number',
}

function fmtDuration(seconds: number | null): string {
  if (!seconds) return '—'
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return m > 0 ? `${m}m ${s}s` : `${s}s`
}

function fmtRelative(iso: string | null): string {
  if (!iso) return '—'
  const diff = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'Just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  return `${Math.floor(hrs / 24)}d ago`
}

function TelecallerDashboard() {
  const navigate = useNavigate()
  const currentUser = authService.getCurrentUser()
  const [data, setData] = useState<MyPerformanceData | null>(null)

  useEffect(() => {
    reportingService.getMyPerformance().then(setData)
  }, [])

  const loading = data === null

  const kpiCards = [
    {
      label: 'Calls Today',
      value: data?.calls_today ?? '—',
      sub: data ? `${data.calls_mtd} this month` : '',
      icon: PhoneCall,
      accent: 'var(--info-text)',
      accentBg: 'var(--info-bg)',
    },
    {
      label: 'Policies Issued',
      value: data?.policies_mtd ?? '—',
      sub: 'Month to date',
      icon: CheckCircle2,
      accent: 'var(--success-text)',
      accentBg: 'var(--success-bg)',
    },
    {
      label: 'Lost Leads',
      value: data?.lost_mtd ?? '—',
      sub: 'Month to date',
      icon: XCircle,
      accent: 'var(--danger-text)',
      accentBg: 'var(--danger-bg)',
    },
    {
      label: 'Conversion Rate',
      value: data ? `${data.conversion_rate}%` : '—',
      sub: 'Issued ÷ closed MTD',
      icon: Target,
      accent: 'var(--brand-600)',
      accentBg: 'var(--brand-primary-subtle)',
    },
    {
      label: 'Open Leads',
      value: data?.open_leads ?? '—',
      sub: 'Assigned & active',
      icon: BarChart2,
      accent: 'var(--warning-text)',
      accentBg: 'var(--warning-bg)',
    },
  ]

  return (
    <div className="space-y-6">
      <div className="section-header">
        <div>
          <h1 className="page-title">My Dashboard</h1>
          <p className="page-subtitle">
            {currentUser?.full_name ?? 'Your'} performance — today &amp; this month
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            className="btn-secondary flex items-center gap-1.5"
            onClick={() => navigate('/workspace')}
          >
            <Users className="h-3.5 w-3.5" />
            My Active Leads
          </button>
          <button
            className="btn-secondary flex items-center gap-1.5"
            onClick={() => navigate('/leads/history')}
          >
            <Clock className="h-3.5 w-3.5" />
            My Closed Leads
          </button>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-4">
        {kpiCards.map(card => {
          const Icon = card.icon
          return (
            <div key={card.label} className="card" style={{ borderTop: `3px solid ${card.accent}` }}>
              <div className="w-8 h-8 rounded-md flex items-center justify-center mb-3"
                style={{ background: card.accentBg }}>
                <Icon className="h-4 w-4" style={{ color: card.accent }} />
              </div>
              {loading
                ? <div className="skeleton h-7 w-16 rounded mb-1" />
                : <p className="stat-card-value">{card.value}</p>
              }
              <p className="stat-card-label mt-1">{card.label}</p>
              {!loading && card.sub && (
                <p className="text-xs mt-0.5" style={{ color: 'var(--text-tertiary)' }}>{card.sub}</p>
              )}
            </div>
          )
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pipeline breakdown */}
        <div className="card">
          <h2 className="text-md font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
            My Pipeline
          </h2>
          {loading && (
            <div className="space-y-2">
              {[1,2,3].map(i => <div key={i} className="skeleton h-10 rounded" />)}
            </div>
          )}
          {!loading && (data?.pipeline.length === 0) && (
            <div className="empty-state">
              <p>No active leads in your pipeline</p>
            </div>
          )}
          {!loading && data && data.pipeline.length > 0 && (() => {
            const total = data.pipeline.reduce((s, p) => s + p.count, 0)
            return (
              <div className="space-y-3">
                {data.pipeline.map(p => {
                  const pct = total > 0 ? Math.round((p.count / total) * 100) : 0
                  const color = STATUS_COLOR[p.status] ?? 'var(--text-secondary)'
                  return (
                    <div key={p.status}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                          {STATUS_LABEL[p.status] ?? p.status}
                        </span>
                        <span className="text-sm tabular-nums" style={{ color }}>
                          {p.count} <span style={{ color: 'var(--text-tertiary)', fontWeight: 400 }}>({pct}%)</span>
                        </span>
                      </div>
                      <div className="w-full rounded-full h-2" style={{ background: 'var(--surface-subtle)' }}>
                        <div
                          className="h-2 rounded-full transition-all"
                          style={{ width: `${pct}%`, background: color }}
                        />
                      </div>
                    </div>
                  )
                })}
                <p className="text-xs pt-1" style={{ color: 'var(--text-tertiary)' }}>
                  Total active: {total} leads
                </p>
              </div>
            )
          })()}
        </div>

        {/* Recent calls */}
        <div className="card">
          <h2 className="text-md font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
            Recent Calls
          </h2>
          {loading && (
            <div className="space-y-2">
              {[1,2,3,4,5].map(i => <div key={i} className="skeleton h-10 rounded" />)}
            </div>
          )}
          {!loading && (data?.recent_calls.length === 0) && (
            <div className="empty-state">
              <p>No calls logged yet</p>
            </div>
          )}
          {!loading && data && data.recent_calls.length > 0 && (
            <div className="space-y-1">
              {data.recent_calls.map((call, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between px-3 py-2.5 rounded-md cursor-pointer"
                  style={{ background: 'var(--surface-subtle)' }}
                  onClick={() => navigate(`/leads/${call.lead_id}`)}
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate" style={{ color: 'var(--text-primary)' }}>
                      {call.customer_name}
                    </p>
                    <p className="text-xs mt-0.5" style={{ color: 'var(--text-tertiary)' }}>
                      {call.disposition ? (DISPOSITION_LABEL[call.disposition] ?? call.disposition) : 'No disposition'}
                      {' · '}{fmtDuration(call.duration_seconds)}
                    </p>
                  </div>
                  <div className="text-right shrink-0 ml-3">
                    <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                      {fmtRelative(call.called_at)}
                    </p>
                    <ChevronRight className="h-3.5 w-3.5 ml-auto mt-0.5" style={{ color: 'var(--text-tertiary)' }} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <PendingDiscountsCTA />
      <NegativeConfirmationsCTA />
      <AutomationStatsCTA />
    </div>
  )
}

// ── Page shell ────────────────────────────────────────────────────────────────

export default function DashboardPage() {
  const currentUser = authService.getCurrentUser()
  const role = currentUser?.role
  if (role === UserRole.BACKEND_ADMIN)    return <AdminDashboard />
  if (role === UserRole.DEALER_PRINCIPAL) return <DPDashboard />
  if (role === UserRole.TELECALLER)       return <TelecallerDashboard />
  return <OperationalDashboard />
}
